import React from 'react';
import styles from './InstagramUserAudienceBreakdown.module.css';
import GenderChart from '../../AudienceCharts/GenderChart';
import AgeChart from '../../AudienceCharts/AgeChart';
import countries from '../../../../../utility/countries';
import languages from '../../../../../utility/languages';
import { numberWithCommas, roundTo2Dp } from '../../../../../helpers';
import ProgressBar from '../../../../../ui/ProgressBar/ProgressBar';
import { colors } from '../../AudienceCharts/helpers';
import { range } from 'lodash';
import { Skeleton } from '@round/ui-kit';
import cn from 'classnames';
import useInstagramUserAnalyticsData from './useInstagramUserAnalyticsData';

type Props = {
    userId: number | undefined | null;
};

const InstagramUserAudienceBreakdown = ({ userId }: Props) => {
    const { analytics, isLoading, error } = useInstagramUserAnalyticsData(userId);

    if (!analytics && !isLoading) {
        return <div className={styles.messageBox}>No data found</div>;
    }

    if (!isLoading && error) {
        return <div className={styles.messageBox}>{error}</div>;
    }

    return (
        <div>
            <section className={styles.chartsSection}>
                <div className={styles.chartContainer}>
                    <p className={styles.chartTitle}>Gender</p>
                    <GenderChart
                        loading={isLoading}
                        className={styles.chart}
                        data={analytics?.profile.audience.gender_age_distribution ?? []}
                    />
                </div>

                <div className={styles.chartContainer}>
                    <p className={styles.chartTitle}>Age</p>
                    <AgeChart
                        loading={isLoading}
                        className={styles.chart}
                        data={analytics?.profile.audience.gender_age_distribution ?? []}
                    />
                </div>
            </section>

            <section className={styles.chartsSection}>
                <div>
                    <p className={styles.chartTitle}>Top Countries</p>
                    <div className={styles.topChart}>
                        {isLoading
                            ? range(0, 5).map((i) => <Skeleton key={i} className={styles.skeleton} />)
                            : analytics?.profile.audience.countries
                                  .sort((a, b) => b.value - a.value)
                                  .slice(0, 5)
                                  .map((country) => (
                                      <div key={country.code}>
                                          <div className={styles.topChartLabels}>
                                              <span>
                                                  <span
                                                      className={cn(
                                                          `fi fi-${country.code.toLowerCase()}`,
                                                          styles.flagIcon
                                                      )}
                                                  />
                                                  {countries[country.code]}
                                              </span>
                                              <span>{numberWithCommas(roundTo2Dp(country.value))}%</span>
                                          </div>

                                          <ProgressBar
                                              barStyles={{ backgroundColor: colors[0] }}
                                              progress={roundTo2Dp(country.value)}
                                          />
                                      </div>
                                  ))}
                    </div>
                </div>

                <div>
                    <p className={styles.chartTitle}>Top Languages</p>
                    <div className={styles.topChart}>
                        {isLoading
                            ? range(0, 5).map((i) => <Skeleton key={i} className={styles.skeleton} />)
                            : analytics?.profile.audience.languages
                                  .sort((a, b) => b.value - a.value)
                                  .slice(0, 5)
                                  .map((language) => (
                                      <div key={language.code}>
                                          <div className={styles.topChartLabels}>
                                              <span>{languages[language.code]?.name}</span>
                                              <span>{numberWithCommas(roundTo2Dp(language.value))}%</span>
                                          </div>
                                          <ProgressBar
                                              barStyles={{
                                                  backgroundColor: colors[1],
                                              }}
                                              progress={roundTo2Dp(language.value)}
                                          />
                                      </div>
                                  ))}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default InstagramUserAudienceBreakdown;
